import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Shield, MessageCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      toast.success('Tekrar hoş geldiniz!');
      if (userData.role === 'admin') navigate('/admin');
      else navigate('/app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Giriş başarısız');
    } finally { setIsLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      toast.success('Hesap oluşturuldu!');
      setTimeout(() => navigate('/app'), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt başarısız');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <div className="relative py-8 sm:py-12 px-4 sm:px-6 flex flex-col items-center justify-center bg-card border-b border-border/70">
        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary flex items-center justify-center">
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary-foreground" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-display">2in1Tech</h1>
        </div>
        <p className="text-muted-foreground text-xs sm:text-sm">Güvenli birleşik mesajlaşma platformu</p>
      </div>
      <div className="flex-1 flex items-start justify-center px-3 sm:px-4 py-6 sm:py-8">
        <div className="w-full max-w-md">
          <Tabs defaultValue="login" className="w-full" data-testid="auth-tabs">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Giriş Yap</TabsTrigger>
              <TabsTrigger value="register">Hesap Oluştur</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="bg-card rounded-xl border border-border/70 p-6 space-y-4 shadow-sm">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">E-posta</label>
                    <Input data-testid="login-email-input" type="email" placeholder="ornek@email.com" value={loginData.email}
                      onChange={e => setLoginData(p => ({...p, email: e.target.value}))} required className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Şifre</label>
                    <Input data-testid="login-password-input" type="password" placeholder="Şifrenizi girin" value={loginData.password}
                      onChange={e => setLoginData(p => ({...p, password: e.target.value}))} required className="rounded-xl" />
                  </div>
                </div>
                <Button data-testid="login-submit-button" type="submit" className="w-full rounded-xl h-12 text-base font-medium" disabled={isLoading}>
                  {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="bg-card rounded-xl border border-border/70 p-6 space-y-4 shadow-sm">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">İsim</label>
                    <Input data-testid="register-name-input" type="text" placeholder="Adınız" value={registerData.name}
                      onChange={e => setRegisterData(p => ({...p, name: e.target.value}))} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">E-posta</label>
                    <Input data-testid="register-email-input" type="email" placeholder="ornek@email.com" value={registerData.email}
                      onChange={e => setRegisterData(p => ({...p, email: e.target.value}))} required className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Şifre</label>
                    <Input data-testid="register-password-input" type="password" placeholder="Şifre oluşturun" value={registerData.password}
                      onChange={e => setRegisterData(p => ({...p, password: e.target.value}))} required className="rounded-xl" />
                  </div>
                </div>
                <Button data-testid="register-submit-button" type="submit" className="w-full rounded-xl h-12 text-base font-medium" disabled={isLoading}>
                  {isLoading ? 'Hesap oluşturuluyor...' : 'Hesap Oluştur'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <div className="mt-6 text-center">
            <button onClick={() => navigate('/admin')} className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Yönetici Portalı
            </button>
          </div>
        </div>
      </div>
      <div className="py-4 px-4 text-center border-t border-border/50">
        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
          <Shield className="w-3 h-3" /><span>Uçtan uca şifreli platform</span>
        </div>
      </div>
    </div>
  );
}
